import { ReactNode, useEffect, useRef } from 'react';
import { Route, useLocation } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';

import {
  ANIMATED_ROOT_POPUP_ID,
  animateRootPopupContentIn,
  animateRootPopupContentOut,
} from '~/components/molecules/BottomSheetDialog/utils';

import { setMenuPath } from '~/redux/slices/location';
import { MenuDialogContainer } from '~/routes/MenuRouter/MenuDialogContainer';
import {
  isMenuPopupPath,
  menuPaths,
  menuRoutes,
} from '~/routes/MenuRouter/menuRoutes';
import SwitchWithLocationTracker from '~/routes/SwitchWithLocationTracker';

import styleConsts from '~/sass/consts.module.scss';

import menuConsts from './menuRouter.consts.module.scss';

export function MenuRouterTransition({
  isIn,
  children,
}: {
  children: ReactNode;
  isIn: boolean;
}): JSX.Element {
  return (
    <CSSTransition
      mountOnEnter
      unmountOnExit
      classNames={menuConsts.menuDialogClass}
      timeout={Number(menuConsts.menuTransitionDuration)}
      in={isIn}
    >
      {children}
    </CSSTransition>
  );
}

export function MenuRouter(): JSX.Element {
  const isRootPopupAnimated = useRef(false);
  const location = useLocation();

  const isIn = isMenuPopupPath(location.pathname);

  // if there is an animatable dialog from the PopupRouter under the menu view, animate it
  useEffect(() => {
    // for example menu popup or a collection popup
    const rootPopup = document.getElementById(
      ANIMATED_ROOT_POPUP_ID,
    ) as HTMLDivElement;
    if (!rootPopup || window.innerWidth < Number(styleConsts.smallMobileView)) {
      return;
    }
    if (isIn) {
      animateRootPopupContentIn(rootPopup);
      // trap the keyboard focus inside the menu router view
      rootPopup.inert = true;
      isRootPopupAnimated.current = true;
    } else if (isRootPopupAnimated.current) {
      // the popup has been animated in and menu router view is being closed
      animateRootPopupContentOut(rootPopup);
      rootPopup.inert = false;
      isRootPopupAnimated.current = false;
    }
  }, [isIn]);

  const isOrderedRoute = useRef(false);
  if (isIn) {
    isOrderedRoute.current = menuRoutes[location.pathname]?.order !== undefined;
  }

  return (
    <MenuRouterTransition isIn={isIn}>
      <MenuDialogContainer fixedHeight={isOrderedRoute.current}>
        <SwitchWithLocationTracker
          location={location}
          setPath={setMenuPath}
          condition={isIn}
        >
          {menuPaths.map((path) => (
            <Route
              path={path}
              component={menuRoutes[path]?.component}
              key={path}
            />
          ))}
        </SwitchWithLocationTracker>
      </MenuDialogContainer>
    </MenuRouterTransition>
  );
}
